'use client'

import { createContext, useContext, useState } from 'react'

type ClientAppSession = {
  isLoggedIn: boolean
  customerAccessToken?: {
    accessToken: string
    refreshToken: string
    token_type: string
    expiresIn: number
    idToken: string
  }
}

type AppContextProps = {
  session: ClientAppSession
  cartIsOpen: boolean
  setCartIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  /** Set true before cartCreate when the intent is to open the cart on create (e.g. stock-holding Add to Cart). Cleared in onCreateComplete. */
  automaticCartOpen: boolean
  setAutomaticCartOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const defaultAppProps: AppContextProps = {
  session: { isLoggedIn: false },
  cartIsOpen: false,
  setCartIsOpen: () => {},
  automaticCartOpen: false,
  setAutomaticCartOpen: () => {},
}

const AppContext = createContext<AppContextProps>(defaultAppProps)

export const useAppContext = () => useContext(AppContext)

export const AppProvider = ({ session, children }: { session: ClientAppSession; children: React.ReactNode }) => {
  const [cartIsOpen, setCartIsOpen] = useState(false)
  const [automaticCartOpen, setAutomaticCartOpen] = useState(false)

  const value = {
    session: Object.assign(defaultAppProps.session, session),
    cartIsOpen,
    setCartIsOpen,
    automaticCartOpen,
    setAutomaticCartOpen,
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}
